import { Input, Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { Tc, TcPerfilUsuarioEnum } from 'src/app/components/core/_infrastructure/types/common';
import { EnumIdsTcEstadoPvCam } from '../../../types/enum';
import { FilterSearchingProgramaVerificacionViewModel } from '../buscador-pvcam.types';

@Component({
    selector: 'app-buscador-pvcam-filter',
    templateUrl: './buscador-pvcam-filter.component.html',
    styleUrls: ['./buscador-pvcam-filter.component.scss'],
})
export class BuscadorPvCamFilterComponent extends ComponentBase implements OnInit {
    @Input() listEstadosPv: Tc[];
    @Input() listBoxEstadosPv: Tc[];
    @Input() listCampanias: Tc[];
    @Input() listComunidades: Tc[];
    @Input() lastFilter: FilterSearchingProgramaVerificacionViewModel;
    @Output() search = new EventEmitter<FilterSearchingProgramaVerificacionViewModel>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado
    @Output() addEstadoPv = new EventEmitter<number>(); // Informa que se ha añadido un estado al listbox
    @Output() deleteEstadoPv = new EventEmitter<any>(); // Informa que se ha eliminado un estado del listbox

    isCollapsed = false;
    listIdsEstadosPv: number[] = [];
    firstDayMonth: Date;
    lastDayMonth: Date;

    constructor(
        private fb: FormBuilder,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.createForm();
        this.recoverLastSearch();
    }

    private createForm(): void {
        this.firstDayMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        let temp = new Date().getMonth() + 1;
        this.lastDayMonth = new Date(new Date(new Date().getFullYear(), temp, 1).getTime() - 0.001 * 60 * 60 * 24);

        this.form = this.fb.group({
            IdEstadoPv: null,
            listIdEstados: [],
            IdCampania: null,
            IdCa: null,
            FechaPresentacionDesde: this.firstDayMonth,
            FechaPresentacionHasta: this.lastDayMonth,
        });
    }

    /**
     * Recover last search from the previous payload
     */
    private recoverLastSearch(): void {
        if (this.lastFilter) {
            this.form.setValue({
                IdEstadoPv: null,
                listIdEstados: this.lastFilter.listIdEstados,
                IdCampania: this.lastFilter.IdCampania,
                IdCa: this.lastFilter.IdCa,
                FechaPresentacionDesde: this.lastFilter.FechaPresentacionDesde,
                FechaPresentacionHasta: this.lastFilter.FechaPresentacionHasta,
            });
            this.form.markAsDirty();

            setTimeout(() => {
                this.onSearch(null);
            }, 200);
        }

        // As soon as the user changes the "filter", the previous lastFilter pagination values has no sense
        this.form.valueChanges.pipe(debounceTime(500)).subscribe(values => {
            if (values) {
                this.clearPaginationFilter();
            }
        });
    }

    /**
     * Manejador del evento para limpiar el formulario.
     * @param $event Evento javascript
     */
    resetForm($event: any): void {
        $event.stopPropagation();
        this.form.setValue({
            IdEstadoPv: null,
            listIdEstados: [],
            IdCampania: null,
            IdCa: null,
            FechaPresentacionDesde: this.firstDayMonth,
            FechaPresentacionHasta: this.lastDayMonth,
        });
        this.form.markAsPristine();

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter() {
        if (!this.lastFilter) {
            return;
        }
        this.lastFilter.PageNumber = 0;
        this.lastFilter.Asc = 0;
        this.lastFilter.OrderBy = '';
    }

    /**
     * Manejador del evento de búsqueda
     * @param $event Evento Javascript
     */
    onSearch($event: any): void {
        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        const payload: FilterSearchingProgramaVerificacionViewModel = {
            ...this.form.value,
            IdUsuario: this.loggedInUser!.Id,
            EsComunidadAutonoma: this.isCA,
            EsEntidadAuditora: this.isEA,
            EsAdmin: this.isAdmin,
            PageNumber: this.lastFilter?.PageNumber || 0,
            Asc: this.lastFilter?.Asc || 0,
            OrderBy: this.lastFilter?.OrderBy || '',
        };

        this.search.emit(payload);
        //RDLF 070425: (tarea 170516) Al hacer la busqueda limpiamos el valor seleccionado en el combo por mayor claridad
        this.form.patchValue({
            IdEstadoPv: null,
        });
    }

    /*
     * Manejador del evento de inserción de estado en el listBox
     */
    onAddEstadoPv(): void {
        const idEstadoPv = this.form.value.IdEstadoPv;
        this.addEstadoPv.emit(idEstadoPv);
        //se añade al filtro de estados
        this.listIdsEstadosPv.push(idEstadoPv);
        this.form.patchValue({
            IdEstadoPv: null,
            listIdEstados: this.listIdsEstadosPv,
        });
        //Si selecciono el estado 'No Presentado' borro las fechas
        if (idEstadoPv == EnumIdsTcEstadoPvCam.ID_NO_PRESENTADO) {
            this.form.patchValue({
                FechaPresentacionDesde: null,
                FechaPresentacionHasta: null,
            });
        }
    }

    /*
     * Manejador del evento de borrado de estado del listBox
     */
    onDeleteEstadoPv(item: Tc) {
        this.deleteEstadoPv.emit(item);
        //se elimina del filtro de estados
        this.listIdsEstadosPv.splice(this.listIdsEstadosPv.indexOf(item.Id), 1);
        this.form.patchValue({
            listIdEstados: this.listIdsEstadosPv,
        });
    }

    /**
     * Para controlar la propiedad disabled del botón de reset.
     * @returns boolean
     */
    formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    /**
     * Para controlar la propiedad disabled del botón de búsqueda.
     * Mientras no estén cargados los estados, no se habilita el botón.
     * @returns boolean
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed || !this.listEstadosPv || this.listEstadosPv?.length == 0;
    }
}
